import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { load } from 'recaptcha-v3';

import isEmpty from 'lodash/isEmpty';
import first from 'lodash/first';

import { Combobox } from '@shared/fields';
import Field from '../fields/Field';
import Btn from '../buttons/Btn';
import Textareafield from '../fields/Textareafield';

import { FONT_FAMILY_MAIN, TEXT_COLOR } from '../../theme';
import { MEETING_DEFAULT_KIND, MEETING_KIND_OPTIONS } from './constants';

const MeetingsSchema = Yup.object().shape({
  firstname: Yup.string()
    .matches(
      /^[A-Za-z\u00C0-\u00FF\-\ ]+$/,
      'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).',
    )
    .required('Ce champ est obligatoire.'),
  lastname: Yup.string()
    .matches(
      /^[A-Za-z\u00C0-\u00FF\-\ ]+$/,
      'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).',
    )
    .required('Ce champ est obligatoire.'),
  email: Yup.string()
    .trim('Ce champ ne peut pas inclure d\'espace au début et à la fin')
    .strict(true)
    .email('Merci de renseigner une adresse email valide.')
    .required('Ce champ est obligatoire.'),
  phone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
      'Le téléphone doit être au format 01 23 45 67 89',
    )
    .required('Ce champ est obligatoire.'),
  kind: Yup.string()
    .oneOf(MEETING_KIND_OPTIONS.map((x) => x.value))
    .required('Ce champ est obligatoire.'),
  message: Yup.string().max(500, 'Le message ne peut pas dépasser 500 caractères.'),
});

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  category: '',
  message: '',
  new_google_recaptcha_token: '',
  kind: '',
};

const Space = styled.div`
  margin-bottom: 24px;
`;

const StyledForm = styled(Form)`
  max-width: 386px;
`;

const Title = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 16px;
  font-weight: 800;
  line-height: 1.38;
  letter-spacing: 0.2px;
  color: ${TEXT_COLOR};
  margin-bottom: 8px;
`;

class MeetingsForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      recaptchaToken: '',
    };
  }

  componentDidMount() {
    this.getRecaptchaToken();
  }

  getRecaptchaToken = async () => {
    const recaptcha = await load(process.env.GOOGLE_RECAPTCHA_SITE_KEY);

    const token = await recaptcha.execute('meeting_creation');

    if (token) {
      this.setState({ recaptchaToken: token });
    }
  };

  render() {
    const smallInputsWidth = '200px';
    const {
      meetingSubjects, cachedUser, onFormSubmit, isSending, bookableMeetingKinds, isMessageAllowed,
    } = this.props;
    const { recaptchaToken } = this.state;
    initialValues.kind = bookableMeetingKinds.includes(MEETING_DEFAULT_KIND) ? MEETING_DEFAULT_KIND : bookableMeetingKinds[0];
    const userData = isEmpty(cachedUser) ? initialValues : cachedUser;

    if (isEmpty(userData.category)) {
      userData.category = first(meetingSubjects).id;
    }

    return (
      <Formik
        initialValues={userData}
        validationSchema={MeetingsSchema}
        onSubmit={(values, formikBag) => onFormSubmit(values, formikBag)}
      >
        {({
          values, errors, touched, handleChange, handleBlur, setFieldValue,
        }) => (
          <StyledForm>
            <Title>Mes coordonnées</Title>
            <Field
              type="hidden"
              name="new_google_recaptcha_token"
              value={recaptchaToken}
            />
            <Field
              type="text"
              label="Prénom"
              placeholder="Martin"
              name="firstname"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstname}
              valid={touched.firstname && !errors.firstname}
              errorMessage={
                errors.firstname && touched.firstname ? errors.firstname : null
              }
            />
            <Field
              type="text"
              label="Nom"
              placeholder="Dupont"
              name="lastname"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastname}
              valid={touched.lastname && !errors.lastname}
              errorMessage={errors.lastname && touched.lastname ? errors.lastname : null}
            />
            <Field
              type="text"
              label="Email"
              placeholder="exemple@exemple.com"
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              valid={touched.email && !errors.email}
              errorMessage={errors.email && touched.email ? errors.email : null}
            />
            {isMessageAllowed && (
            <Textareafield
              rows={3}
              type="text"
              label="Message (facultatif)"
              placeholder="Précisez le motif de votre demande de renseignements et toute autre information utile"
              name="message"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.message}
              valid={touched.message && !errors.message}
              errorMessage={errors.message && touched.message ? errors.message : null}
            />
            )}
            <Space />
            <Field
              type="text"
              label="Numéro de téléphone"
              placeholder="01 23 45 67 89"
              width={smallInputsWidth}
              name="phone"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phone}
              valid={touched.phone && !errors.phone}
              errorMessage={errors.phone && touched.phone ? errors.phone : null}
            />
            <Combobox
              name="category"
              label="Pour quelle formation?"
              options={meetingSubjects}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.category}
              valid={touched.category && !errors.category}
              errorMessage={errors.category && touched.category ? errors.category : null}
            />
            <Combobox
              name="kind"
              label="Vous souhaitez prendre"
              options={MEETING_KIND_OPTIONS.filter((x) => bookableMeetingKinds.includes(x.value))}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.kind}
              valid={touched.kind && !errors.kind}
              errorMessage={errors.kind && touched.kind ? errors.kind : null}
            />
            <Btn
              type="submit"
              text="Prendre rendez-vous"
              block
              disabled={isSending}
              isLoading={isSending}
              onClick={() => setFieldValue('new_google_recaptcha_token', recaptchaToken)}
            />
          </StyledForm>
        )}
      </Formik>
    );
  }
}

MeetingsForm.propTypes = {
  meetingSubjects: PropTypes.array,
  cachedUser: PropTypes.object,
  onFormSubmit: PropTypes.func,
  isSending: PropTypes.bool,
  bookableMeetingKinds: PropTypes.array,
  isMessageAllowed: PropTypes.bool,
};

MeetingsForm.defaultProps = {
  meetingSubjects: [],
  cachedUser: {},
  onFormSubmit: () => {},
  isSending: false,
  bookableMeetingKinds: [],
  isMessageAllowed: false,
};

export default MeetingsForm;
